import { useState, useEffect, useRef, lazy, Suspense } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Plus, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/NotificationBell";
import { useTranslation } from "react-i18next";

function LanguageSwitcher({ disabled = false }: { disabled?: boolean }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("pt") ? "pt" : "en";

  const toggleLanguage = () => {
    const nextLang = currentLang === "pt" ? "en" : "pt";
    i18n.changeLanguage(nextLang);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleLanguage}
      disabled={disabled}
      className="h-8 px-2 text-xs font-semibold gap-1.5 text-muted-foreground hover:text-foreground"
      aria-label={`Trocar idioma (atual: ${currentLang.toUpperCase()})`}
    >
      <Globe className="h-3.5 w-3.5" />
      <span>{currentLang.toUpperCase()}</span>
    </Button>
  );
}

// Lazy-loaded components that contain heavy Radix UI elements (only downloaded when needed)
const AuthModal = lazy(() => import("@/components/AuthModal").then(m => ({ default: m.AuthModal })));
const BecomeProducerModal = lazy(() => import("@/components/BecomeProducerModal").then(m => ({ default: m.BecomeProducerModal })));
const UserMenuActions = lazy(() => import("@/components/UserMenuActions").then(m => ({ default: m.UserMenuActions })));
const MobileUserMenu = lazy(() => import("@/components/UserMenuActions").then(m => ({ default: m.MobileUserMenu })));

// Prefetch do chunk JS da rota ao hover — elimina o download no momento do clique
const routePrefetchMap: Record<string, () => Promise<unknown>> = {
  "/eventos":   () => import("@/pages/Eventos"),
  "/revenda":   () => import("@/pages/Revenda"),
  "/produtores":() => import("@/pages/Produtores"),
  "/":          () => import("@/pages/Index"),
};
const prefetched = new Set<string>();
function prefetchRoute(href: string) {
  if (prefetched.has(href) || !routePrefetchMap[href]) return;
  prefetched.add(href);
  routePrefetchMap[href]();
}

export function Navbar() {
  const { t } = useTranslation("common");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("login");
  const [navSearch, setNavSearch] = useState("");
  const [producerModalOpen, setProducerModalOpen] = useState(false);
  const [isDemoImmersive, setIsDemoImmersive] = useState(false);
  const { user, profile, role, allRoles, switchRole, signOut } = useAuth();
  const { itemCount } = useCart();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const handleCreateEvent = () => {
    if (role === "producer") {
      navigate("/produtor/eventos/novo");
    } else {
      setProducerModalOpen(true);
    }
  };

  const isAdminOrProducer = location.pathname.startsWith("/admin") || location.pathname.startsWith("/producer") || location.pathname.startsWith("/produtor");
  const isLandingPage = location.pathname === "/";

  useEffect(() => {
    if (searchParams.get("login") === "true") {
      setAuthOpen(true);
      // Clean up query param
      const params = new URLSearchParams(location.search);
      params.delete("login");
      navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true, state: location.state });
    }
  }, [searchParams, navigate, location.pathname, location.search, location.hash, location.state]);

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 20);
    handle();
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  useEffect(() => {
    const onDemoImmersive = (e: Event) => {
      const custom = e as CustomEvent<{ immersive: boolean }>;
      setIsDemoImmersive(Boolean(custom.detail?.immersive));
    };
    window.addEventListener("demo-immersive-change", onDemoImmersive);
    return () => window.removeEventListener("demo-immersive-change", onDemoImmersive);
  }, []);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  const links = [
    { label: t("navbar.links.events"), href: "/eventos" },
    { label: t("navbar.links.resale"), href: "/revenda" },
    { label: t("navbar.links.producers"), href: "/produtores" },
  ];

  const navLinks = links;

  const openLogin = () => { setAuthTab("login"); setAuthOpen(true); };
  const openRegister = () => { setAuthTab("register"); setAuthOpen(true); };

  const handleNavSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (navSearch.trim()) {
      navigate(`/eventos?q=${encodeURIComponent(navSearch.trim())}`);
      setNavSearch("");
    }
  };

  const roleLabels: Record<string, string> = {
    admin: t("navbar.roles.admin"),
    producer: t("navbar.roles.producer"),
    staff: t("navbar.roles.staff"),
    buyer: t("navbar.roles.buyer"),
  };

  const roleDashboardLinks: Record<string, string> = {
    admin: "/admin/relatorios",
    producer: "/produtor/dashboard",
    staff: "/staff",
    buyer: "/meus-ingressos",
  };


  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const firstName = profile?.full_name
    ? profile.full_name.trim().split(/\s+/)[0]
    : t("navbar.account.defaultName");

  const accountDisplayName = profile?.full_name
    ? firstName
    : t("navbar.account.myAccount");

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter,transform,opacity] duration-300 ease-out motion-reduce:transition-none",
          isDemoImmersive ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100",
          scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
        )}
      >
        <div className="container flex h-14 lg:h-16 items-center justify-between">
          <Link to="/" onMouseEnter={() => prefetchRoute("/")} className="shrink-0">
            <TicketHallLogo size="lg" className="hidden lg:block" />
            <TicketHallLogo size="md" className="lg:hidden" />
          </Link>

          {/* Desktop Navigation — visible at lg+ */}
          <nav className="hidden lg:flex items-center gap-6">
            {!isLandingPage && !isAdminOrProducer && (
              <form onSubmit={handleNavSearch} aria-hidden={scrolled || undefined} className={cn("relative overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out motion-reduce:transition-none", scrolled ? "max-w-0 -translate-y-1 opacity-0 pointer-events-none" : "max-w-[180px] translate-y-0 opacity-100")}>
                <SearchInput
                  type="text"
                  disabled={scrolled}
                  value={navSearch}
                  onChange={(e) => setNavSearch(e.target.value)}
                  placeholder={t("navbar.search.placeholder")}
                  className="h-8 w-[180px] text-sm rounded-full bg-muted/50 border-transparent focus:border-border focus:bg-background transition-all"
                />
              </form>
            )}
            {navLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                onMouseEnter={() => prefetchRoute(l.href)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions — visible at lg+ */}
          <div className="hidden lg:flex items-center gap-2">
            <div aria-hidden={scrolled || undefined} className={cn("overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out motion-reduce:transition-none", scrolled ? "max-w-0 -translate-y-1 opacity-0 pointer-events-none" : "max-w-20 translate-y-0 opacity-100")}>
              <LanguageSwitcher disabled={scrolled} />
            </div>
            {user ? (
              <Suspense fallback={<div className="w-[120px] h-8 bg-muted/40 animate-pulse rounded-md" />}>
                <UserMenuActions
                  itemCount={itemCount}
                  accountDisplayName={accountDisplayName}
                  roleLabels={roleLabels}
                  role={role}
                  profile={profile}
                  initials={initials}
                  firstName={firstName}
                  user={user}
                  handleCreateEvent={handleCreateEvent}
                  allRoles={allRoles}
                  switchRole={switchRole}
                  roleDashboardLinks={roleDashboardLinks}
                  signOut={signOut}
                  navigate={navigate}
                />
              </Suspense>
            ) : (
              <>
                <Link 
                  to="/carrinho" 
                  className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={itemCount > 0 ? t("navbar.cart.ariaLabelWithCount", { count: itemCount }) : t("navbar.cart.ariaLabel")}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </Link>
                <Button variant="outline" size="sm" onClick={handleCreateEvent} className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  {t("navbar.cta.createEvent")}
                </Button>
                <Button variant="ghost" size="sm" onClick={openLogin}>{t("navbar.auth.login")}</Button>
                <Button variant="default" size="sm" onClick={openRegister}>{t("navbar.auth.register")}</Button>
              </>
            )}
          </div>

          {/* Mobile/Tablet Header — visible below lg */}
          <div className="lg:hidden flex items-center gap-1">
            <div aria-hidden={scrolled || undefined} className={cn("overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out motion-reduce:transition-none", scrolled ? "max-w-0 -translate-y-1 opacity-0 pointer-events-none" : "max-w-20 translate-y-0 opacity-100")}>
              <LanguageSwitcher disabled={scrolled} />
            </div>

            {/* Cart icon for logged-out users on mobile */}
            {!user && !isAdminOrProducer && (
              <Link
                to="/carrinho"
                className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={itemCount > 0 ? t("navbar.cart.ariaLabelWithCount", { count: itemCount }) : t("navbar.cart.ariaLabel")}
              >
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
              </Link>
            )}

            {user && <NotificationBell />}

            {!user && !isAdminOrProducer && (
              <Button variant="default" size="sm" className="h-11 min-h-11 bg-transparent px-0 text-xs hover:bg-transparent" onClick={openLogin}>
                <span className="inline-flex h-8 items-center justify-center rounded-lg bg-primary px-3 transition-colors hover:bg-primary/90">
                  {t("navbar.auth.login")}
                </span>
              </Button>
            )}

            {/* Hamburger menu — always visible on mobile */}
            <button ref={mobileMenuButtonRef} aria-expanded={mobileOpen} aria-haspopup="dialog" className="lg:hidden flex h-11 w-11 items-center justify-center rounded-xl text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? t("navbar.mobileMenu.close") : t("navbar.mobileMenu.open")}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-background" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              onCloseAutoFocus={(event) => { event.preventDefault(); if (!authOpen && !producerModalOpen) mobileMenuButtonRef.current?.focus(); }}
              className="fixed inset-0 z-[70] flex h-[100dvh] flex-col bg-background outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none"
            >
              <DialogPrimitive.Title className="sr-only">{t("navbar.mobileMenu.open")}</DialogPrimitive.Title>
              <div className="container flex h-14 shrink-0 items-center justify-between border-b border-border/60">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex min-h-11 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <TicketHallLogo size="md" />
                </Link>
                <div className="flex items-center gap-2">
                  <LanguageSwitcher />
                  <DialogPrimitive.Close className="flex h-11 w-11 items-center justify-center rounded-xl text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={t("navbar.mobileMenu.close")}>
                    <X className="h-5 w-5" aria-hidden="true" />
                  </DialogPrimitive.Close>
                </div>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain" style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
                {!user ? (
                  <div className="container mx-auto flex w-full max-w-lg flex-1 flex-col gap-6 pt-6">
                    <nav aria-label={t("navbar.mobileMenu.open")} className="space-y-1">
                      {[{ label: t("navbar.mobileMenu.home"), href: "/" }, ...navLinks].map((link) => (
                        <Link key={link.href} to={link.href} onMouseEnter={() => prefetchRoute(link.href)} onClick={() => setMobileOpen(false)} aria-current={location.pathname === link.href ? "page" : undefined}
                          className={cn("flex min-h-14 items-center rounded-xl px-3 py-3 font-display text-xl font-medium tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted", location.pathname === link.href ? "bg-muted text-foreground" : "text-muted-foreground")}>
                          {link.label}
                        </Link>
                      ))}
                    </nav>
                    <div className="mt-auto space-y-4 border-t border-border/60 pt-5">
                      <Button variant="ghost" className="min-h-12 w-full justify-start rounded-xl px-3 text-sm" onClick={() => { setMobileOpen(false); handleCreateEvent(); }}>
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t("navbar.cta.createEvent")}
                      </Button>
                      <div className="grid grid-cols-2 gap-3">
                        <Button variant="outline" className="min-h-12 h-auto whitespace-normal rounded-xl py-3" onClick={() => { setMobileOpen(false); openLogin(); }}>{t("navbar.auth.login")}</Button>
                        <Button className="min-h-12 h-auto whitespace-normal rounded-xl py-3" onClick={() => { setMobileOpen(false); openRegister(); }}>{t("navbar.auth.register")}</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Suspense fallback={<div className="flex flex-1 items-center justify-center" role="status">{t("navbar.account.myAccount")}</div>}>
                    <MobileUserMenu
                      navLinks={navLinks}
                      prefetchRoute={prefetchRoute}
                      setMobileOpen={setMobileOpen}
                      allRoles={allRoles}
                      role={role}
                      switchRole={switchRole}
                      navigate={navigate}
                      roleDashboardLinks={roleDashboardLinks}
                      roleLabels={roleLabels}
                      signOut={signOut}
                    />
                  </Suspense>
                )}
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      </header>

      <Suspense fallback={null}>
        {authOpen && <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />}
        {producerModalOpen && <BecomeProducerModal open={producerModalOpen} onOpenChange={setProducerModalOpen} />}
      </Suspense>
    </>
  );
}
